import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { ContractsContractVersionUpdate } from 'generated/resources/datatable.graphql'
import { JobCatalogTreeLevelNodeFragment } from 'generated/resources/graphql'

import { mapUpdate } from 'modules/contracts/lib/ContractHistoryUtils'
import getIgnoredKeys from 'modules/contracts/lib/ignoredKeys'

interface Props {
  updates: ContractsContractVersionUpdate[]
  contractVersionId: string
  legalEntityId: string
  country: string
  hasDevJobCatalogTree: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
  hasDevTimeTrackingWorkableDaysDefinition: boolean
  jobCatalogTreeLevelNodes?: JobCatalogTreeLevelNodeFragment[]
  hasCompensationsManagement: boolean
}

const ContractVersionUpdates = ({
  updates,
  contractVersionId,
  legalEntityId,
  country,
  hasDevJobCatalogTree,
  hasTimeTrackingAnnualisationDailyValue,
  hasDevTimeTrackingWorkableDaysDefinition,
  jobCatalogTreeLevelNodes,
  hasCompensationsManagement,
}: Props) => {
  const ignoredKeys = React.useMemo(
    () =>
      getIgnoredKeys(
        country,
        hasDevJobCatalogTree,
        hasTimeTrackingAnnualisationDailyValue,
        hasDevTimeTrackingWorkableDaysDefinition,
        hasCompensationsManagement
      ),
    [
      country,
      hasDevJobCatalogTree,
      hasTimeTrackingAnnualisationDailyValue,
      hasDevTimeTrackingWorkableDaysDefinition,
      hasCompensationsManagement,
    ]
  )

  const filteredUpdates = React.useMemo(
    () => updates.filter((update) => !ignoredKeys.includes(update.field)),
    [updates, ignoredKeys]
  )

  const formattedChanges = React.useMemo(
    () =>
      filteredUpdates
        .map((update) =>
          mapUpdate(update, filteredUpdates, legalEntityId, jobCatalogTreeLevelNodes)
        )
        .filter((u): u is NonNullable<typeof u> => Boolean(u)),
    [filteredUpdates, legalEntityId, jobCatalogTreeLevelNodes]
  )

  return formattedChanges.map((update, index) => {
    if (!update) return null

    const hasCurrentValue = !!update.newValue
    const hasPreviousValue = !!update.oldValue

    if (!hasCurrentValue && !hasPreviousValue) return null

    return (
      <Box
        key={`${update.field}-${index}-${contractVersionId}`}
        component="li"
        style={{ display: 'list-item', listStyleType: 'disc' }}
      >
        <Text.Normal size="100" weight="medium" color="grey800">
          <Text.Normal size="100" weight="medium">
            {update.field}:&nbsp;
          </Text.Normal>
          {hasPreviousValue && (
            <>
              <Text.Normal size="100" stroked>
                {update.oldValue}
              </Text.Normal>
              {hasCurrentValue && <Text.Normal size="100" weight="bold">{`->`}</Text.Normal>}
            </>
          )}
          {hasCurrentValue && (
            <Text.Normal size="100" weight="bold">
              {update.newValue}
            </Text.Normal>
          )}
        </Text.Normal>
      </Box>
    )
  })
}

export default ContractVersionUpdates
